import {
  Component,
  input,
  output,
  HostListener,
  effect,
  inject,
  ElementRef,
  AfterViewInit,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TPipe } from '../../pipes/t.pipe';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<ModalSize, string> = {
  sm: '420px',
  md: '560px',
  lg: '760px',
  xl: '1100px',
};

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, TPipe],
  templateUrl: './app-modal.component.html',
  host: {
    class: 'fixed inset-0 flex items-center justify-center p-4',
    '[class.hidden]': '!visible()',
    /**
     * z-index inline: o Tailwind JIT muitas vezes não gera `z-[110]` quando só aparece no `host` como
     * `[class.z-[110]]`, e o modal de confirmação ficava atrás do primeiro (só overlay / tela esfumada).
     */
    '[style.z-index]': 'stackAbove() ? "140" : "50"',
  },
  styles: [
    `
      /* Mobile / telas pequenas: ~80% da largura do viewport; altura máx. 90vh; desktop: largura do token size (sm/md/lg). */
      .app-modal__panel {
        width: 100%;
        max-width: min(80vw, 100%);
        max-height: 90vh;
      }
      @media (min-width: 768px) {
        .app-modal__panel {
          max-width: min(var(--app-modal-max-w, 560px), 100%);
        }
      }
    `,
  ],
})
export class AppModalComponent implements AfterViewInit {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  /** Se o modal está visível */
  visible = input<boolean>(false);
  /** sm: 420px | md: 560px | lg: 760px | xl: 1100px */
  size = input<ModalSize>('md');
  /** Quando false, oculta a área de footer (ex.: modais só com corpo). */
  showFooter = input<boolean>(true);
  /** Fechar ao clicar no overlay (default true) */
  closeOnOverlay = input<boolean>(true);
  /** Mostrar botao X no header (default true) */
  showCloseButton = input<boolean>(true);
  /**
   * Quando true, o corpo não rola como um todo: fica `overflow-hidden` em coluna flex
   * para o conteúdo interno controlar scroll (ex.: modal de detalhe em duas colunas).
   */
  bodyFill = input(false);
  /**
   * Confirmações curtas: o corpo não cresce com `flex-1` (evita faixa vazia entre header e footer).
   */
  shrinkWrapBody = input(false);
  /** Título do header */
  title = input<string>('');
  /** Subtítulo (muted) */
  subtitle = input<string>('');
  /**
   * Elevar acima de outro `app-modal` (confirmação sobre lista de membros, etc.).
   * Aplica `z-index` maior no host (inline); sem isto o segundo modal pode ficar atrás do primeiro.
   */
  stackAbove = input(false);
  /** Quando false, ESC não fecha (ex.: modal principal enquanto há confirmação empilhada). */
  closeOnEscape = input(true);

  closed = output<void>();

  readonly sizePx = () => SIZE_PX[this.size()];

  constructor() {
    effect(() => {
      if (this.stackAbove()) return;
      const v = this.visible();
      document.body.style.overflow = v ? 'hidden' : '';
    });

    this.destroyRef.onDestroy(() => {
      if (this.stackAbove()) return;
      document.body.style.overflow = '';
    });
  }

  ngAfterViewInit(): void {
    const el = this.el.nativeElement;
    if (!el.querySelector('[autofocus]')) {
      const firstFocusable = el.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement | null;
      firstFocusable?.focus();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible() && this.closeOnEscape()) this.closed.emit();
  }

  onOverlayClick(): void {
    if (this.closeOnOverlay()) this.closed.emit();
  }

  onContentClick(event: Event): void {
    event.stopPropagation();
  }

  close(): void {
    this.closed.emit();
  }
}
